import * as Application from 'expo-application'
import Constants from 'expo-constants'
import * as Updates from 'expo-updates'

import DocumentType from '@/enums/documentType'

const { expoGoConfig } = Constants

export function ApiRoot() {
  const ip = expoGoConfig?.debuggerHost?.split(':')?.[0]

  if (ip) {
    return `http://${ip}:8080`
  } else {
    return process.env.EXPO_PUBLIC_API_ROOT || 'http://localhost:8080'
  }
}

export enum Feature {
  DatadogRUM = 'datadog_rum',
  Employee = 'employee',
  FirstRunExperience = 'first_run_experience',
  PasswordAuth = 'password_auth',
  WebApp = 'web_app',
}

export const AppVersions = {
  expoVersion: Constants.expoConfig?.version,
  gitSha: Constants.expoConfig?.extra?.eas?.gitSha,
  nativeVersion: Application.nativeApplicationVersion,
  runtimeVersion: Updates.runtimeVersion,
}

export const DocumentSelectorTypes: { label: string; value: DocumentType }[] = [
  { label: 'Adoption Contract', value: DocumentType.AdoptionContract },
  { label: 'Medical Record', value: DocumentType.MedicalRecord },
  { label: 'Intake Document', value: DocumentType.IntakeDocument },
  { label: 'Owner Surrender Form', value: DocumentType.OwnerSurrenderForm },
  { label: 'Trial Run Agreement', value: DocumentType.TrialRunAgreement },
]

export const AppTourKeys = {
  DashboardGrid: 'dashboard-grid',
  FosterProfileTab: 'foster-profile-tab',
  FosterHealthTab: 'foster-health-tab',
  FosterGridAdvancedFilter: 'foster-grid-advanced-filter',
  FosterGridBasicFilter: 'foster-grid-filter-basic',
  FosterAdoptionTab: 'foster-adoption-tab',
  FosterDocumentsTab: 'foster-documents-tab',
  PushNotificationModal: 'push-notification-modal',
  MedicationChangeModal: 'medication-change-modal',
} as const

export type TourStep = {
  name: string
  order: number
  text: string
  page?: string
}

export const AppTour = {
  [AppTourKeys.DashboardGrid]: {
    'dashboard-tab': {
      name: 'dashboard-tab',
      order: 0,
      text: 'This is your dashboard, which will show all upcoming tasks for your fosters (vaccines, preventions, etc).',
      page: '/dashboard',
    },
    'dashboard-admin-buttons': {
      name: 'dashboard-admin-buttons',
      order: 1,
      text: 'As an admin, you can toggle between seeing just the tasks for your own fosters, and tasks for all of the animals in your rescue here.',
      page: '/dashboard',
    },
    'foster-tab-button': {
      name: 'foster-tab-button',
      order: 2,
      text: 'All of the animals in your rescue can be found in this tab.',
      page: '/fosters',
    },
    'search-button': {
      name: 'search-button',
      order: 3,
      text: 'You can search by foster name, volunteer name, or microchip ID.',
      page: '/fosters',
    },
    'filter-button': {
      name: 'filter-button',
      order: 4,
      text: 'You can find various filters here, which can be used to filter the fosters shown in the grid below.',
      page: '/fosters',
    },
    'foster-grid-filter-basic': {
      name: 'foster-grid-filter-basic',
      order: 5,
      text: 'The grid view filters here allow you to filter by basic information like species, adoption status, and more.',
      page: '/fosters/filters?type=basic',
    },
    'foster-grid-filter-view-all': {
      name: 'foster-grid-filter-view-all',
      order: 6,
      text: 'By default, the grid view shows only your fosters, but you can toggle it to show all fosters in the organization.',
      page: '/fosters/filters?type=basic',
    },
    'foster-grid-filter-advanced': {
      name: 'foster-grid-filter-advanced',
      order: 7,
      text: "If you're looking for more detailed filtering you can try out the advanced filters here.",
      page: '/fosters/filters?type=basic',
    },
    'foster-grid-filter-advanced-add': {
      name: 'foster-grid-filter-advanced-add',
      order: 8,
      text: "You can add as many filters as you want here with the 'Add Another Filter' button.",
      page: '/fosters/filters?type=advanced',
    },
    'foster-grid-filter-advanced-basic': {
      name: 'foster-grid-filter-advanced-basic',
      order: 9,
      text: 'or, go back to the basic filters at any time with the button here.',
      page: '/fosters/filters?type=advanced',
    },
    'foster-grid-filter-reset': {
      name: 'foster-grid-filter-reset',
      order: 10,
      text: "When you're done filtering, you can reset the filters here.",
      page: '/fosters/filters?type=basic',
    },
    'user-settings': {
      name: 'user-settings',
      order: 11,
      text: "Here you'll find settings, including notification preferences and profile picture.",
      page: '/fosters',
    },
    'new-foster-button': {
      name: 'new-foster-button',
      order: 12,
      text: 'Add a new foster to your rescue using the action button here.',
      page: '/fosters',
    },
  },
  [AppTourKeys.FosterProfileTab]: {
    'foster-profile-tab': {
      name: 'foster-profile-tab',
      order: 0,
      text: 'Add a profile picture, basic info, and notes in the Profile tab. All of your changes will be auto-saved.',
      page: undefined,
    },
    'foster-health-tab': {
      name: 'foster-health-tab',
      order: 1,
      text: "Track your fosters' medical care in the health tab.",
      page: undefined,
    },
    'foster-adoptions-tab': {
      name: 'foster-adoptions-tab',
      order: 2,
      text: 'When your animal has been adopted, add some adoption info here. This allows the org to track adoptions and easily connect with adopters.',
      page: undefined,
    },
    'foster-documents-tab': {
      name: 'foster-documents-tab',
      order: 3,
      text: 'Scan or upload any documents here.',
      page: undefined,
    },
    'foster-fab-button': {
      name: 'foster-fab-button',
      order: 4,
      text: "This menu has common shortcuts and actions for your foster.\n\nQuickly print a cover sheet to give to your adopters that contains all of the info they'll need when they take your foster home.\n\nIf your foster passes away or gets returned, this is where you would find the “Mark deceased” and “Mark as Returned” options as well. There's also an option to delete a foster here.",
      page: undefined,
    },
  },
  [AppTourKeys.FosterHealthTab]: {
    'foster-health-tab': {
      name: 'foster-health-tab',
      order: 0,
      text: "Make sure to add your foster's microchip ID here! In the event that this animal gets lost before their adopter registers their microchip, this allows the rescue to search by microchip number to quickly get the pet back to their new family as quickly as possible.",
      page: undefined,
    },
    'foster-fecal-medical-info': {
      name: 'foster-fecal-medical-info',
      order: 1,
      text: 'This text is here to help your volunteers know what medical care their foster should be getting - as an admin, you can customize this text in your organization settings.',
      page: undefined,
    },
    'foster-health-medications': {
      name: 'foster-health-medications',
      order: 2,
      text: 'You can track all medications and preventions here. Tap on an existing medication to edit it, or use the "Add Medication" button to add a new one. You can set up recurring medications (like flea/tick/heartworm preventions) to automatically remind you when the next dose is due.',
      page: undefined,
    },
    'foster-health-vaccinations': {
      name: 'foster-health-vaccinations',
      order: 3,
      text: 'Once you\'ve entered a species & a birth date for your foster, Rescuebase automatically generates a vaccination schedule. Tap on a vaccination to modify it, and then tap the "Update Schedule" button to have Rescuebase automatically add or update the next vaccines in the correct sequence.',
      page: undefined,
    },
  },
  [AppTourKeys.FosterAdoptionTab]: {
    'foster-adoption-tab': {
      name: 'foster-adoption-tab',
      order: 0,
      text: 'As an admin, you can mark a pet as ready for adoption or adopted with the checkboxes here. Filtering by these can help you quickly find adoptable pets within your rescue, or pets that have been adopted.',
      page: undefined,
    },
  },
  [AppTourKeys.FosterDocumentsTab]: {
    'foster-documents-tab-scan-button': {
      name: 'foster-documents-tab-scan-button',
      order: 0,
      text: 'Use this button to allow us to help you get good scan-quality images of any paper documents you may have - this is a great option for adding vet records and intake documents. You can upload as many pages at a time as you want.',
      page: undefined,
    },
    'foster-documents-tab-ai-explanation': {
      name: 'foster-documents-tab-ai-explanation',
      order: 1,
      text: "After uploading, we use AI to determine what type of document you've uploaded, and add some notes about what the document includes. You can long press your document to modify this info, or delete it entirely, or tapping the document will give you the options to download or share the document.",
      page: undefined,
    },
  },
  // TODO: With the new page-based quick settings we can probably actually hook up a tour now
  // [AppTourKeys.SettingsModal]: {
  //   'settings-modal-manage-org': {
  //     name: 'settings-modal',
  //     order: 0,
  //     text: 'As an admin, you can manage your organization info, and Rescuebase subscription here.',
  //     page: undefined,
  //   },
  //   'settings-modal-manage-users': {
  //     name: 'settings-modal-manage-users',
  //     order: 1,
  //     text: 'As an admin, you can add and remove volunteers from your organization here.',
  //     page: undefined,
  //   },
  // },
}

export const KeyboardOffset = 62
